// ============================================================================
// Alarm Templates Module
// ============================================================================
// Management of reusable alarm templates for sensors

import { auth } from '../core/auth.js';
import { t } from '../core/i18n.js';
import { showError } from '../core/ui.js';
import { updatePageTitle, updateActiveNavigation } from './navigation.js';

// Global storage for templates (for filtering)
let alarmTemplates = [];

// ============================================================================
// Helper Functions
// ============================================================================

function formatWeekdays(weekdays) {
    if (!weekdays) return '-';
    const days = [];
    const dayNames = {
        monday: 'Mon',
        tuesday: 'Tue',
        wednesday: 'Wed',
        thursday: 'Thu',
        friday: 'Fri',
        saturday: 'Sat',
        sunday: 'Sun'
    };
    
    for (const [day, enabled] of Object.entries(weekdays)) {
        if (enabled) {
            days.push(dayNames[day]);
        }
    }
    
    return days.join(', ') || 'None';
}

// ============================================================================
// Main View
// ============================================================================

// Main entry point (alias for consistency with other modules)
export async function showAlarmTemplates() {
    return showSetupAlarms();
}

export async function showSetupAlarms() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;

    if (!auth.isAuthenticated()) {
        showError('Please login to manage alarm templates');
        return;
    }

    mainContent.innerHTML = `
        <div class="bg-white shadow rounded-lg p-6">
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div class="flex-1">
                    <h2 class="text-lg font-medium text-gray-900">${t('alarmTemplates.templates')}</h2>
                </div>
                
                <!-- Filter by sensor type -->
                <div class="flex items-center gap-2">
                    <label for="alarm-type-filter" class="text-sm text-gray-700 whitespace-nowrap">${t('alarmTemplates.filterByType')}</label>
                    <select id="alarm-type-filter" onchange="window.filterAlarmTemplates()" class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                        <option value="all">${t('alarmTemplates.allTypes')}</option>
                        <option value="card_reader">${t('alarmTemplates.cardReader')}</option>
                        <option value="temperature">${t('alarmTemplates.temperature')}</option>
                        <option value="temperature_humidity">${t('alarmTemplates.temperatureHumidity')}</option>
                        <option value="tracker">${t('alarmTemplates.tracker')}</option>
                        <option value="missing_checkin_report">${t('alarmTemplates.missingCheckin')}</option>
                    </select>
                </div>
                
                <button id="create-template-btn" onclick="window.showCreateAlarmTemplateModal()" class="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 whitespace-nowrap">
                    <svg class="w-4 h-4 inline-block mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    ${t('alarmTemplates.createTemplate')}
                </button>
            </div>
            
            <div id="alarm-templates-list" class="space-y-4">
                <div class="text-center py-8 text-gray-500">${t('alarmTemplates.loadingTemplates')}</div>
            </div>
        </div>
    `;

    updateActiveNavigation('setup-alarms');
    updatePageTitle(t('alarmTemplates.title'));
    await loadAlarmTemplates();
}

// ============================================================================
// Data Loading
// ============================================================================

export async function loadAlarmTemplates() {
    try {
        const response = await fetch('/api/alarm-templates/templates', {
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to load alarm templates');
        }

        const templates = await response.json();
        alarmTemplates = templates;
        renderAlarmTemplates(templates);

    } catch (error) {
        console.error('Error loading alarm templates:', error);
        const listContainer = document.getElementById('alarm-templates-list');
        if (listContainer) {
            listContainer.innerHTML = `<div class="text-center py-8 text-red-600">${error.message}</div>`;
        }
    }
}

// ============================================================================
// Filter Function
// ============================================================================

export function filterAlarmTemplates() {
    const filterSelect = document.getElementById('alarm-type-filter');
    const filterValue = filterSelect ? filterSelect.value : 'all';
    
    let filteredTemplates = alarmTemplates;
    
    if (filterValue === 'missing_checkin_report') {
        filteredTemplates = alarmTemplates.filter(template => template.alarm_type === 'missing_checkin_report');
    } else if (filterValue !== 'all') {
        filteredTemplates = alarmTemplates.filter(template =>
            template.sensor_type === filterValue && template.alarm_type !== 'missing_checkin_report'
        );
    }
    
    renderAlarmTemplates(filteredTemplates);
}

// ============================================================================
// Display Functions
// ============================================================================

function renderAlarmTemplates(templates) {
    const listContainer = document.getElementById('alarm-templates-list');
    if (!listContainer) return;

    if (templates.length === 0) {
        const filterSelect = document.getElementById('alarm-type-filter');
        const filterValue = filterSelect ? filterSelect.value : 'all';
        
        if (filterValue !== 'all') {
            listContainer.innerHTML = `
                <div class="text-center py-8 text-gray-500">
                    <svg class="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                    <p class="text-sm">${t('alarmTemplates.noTemplatesFilter')}</p>
                    <p class="text-xs mt-1">${t('alarmTemplates.tryDifferentFilter')}</p>
                </div>
            `;
        } else {
            listContainer.innerHTML = `
                <div class="text-center py-8 text-gray-500">
                    <svg class="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
                    </svg>
                    <p class="text-sm">${t('alarmTemplates.noTemplatesYet')}</p>
                    <p class="text-xs mt-1">${t('alarmTemplates.createFirstTemplate')}</p>
                </div>
            `;
        }
        return;
    }
    
    listContainer.innerHTML = templates.map(template => {
        const isAccountLevel = template.alarm_type === 'missing_checkin_report';
        const sensorTypeLabels = {
            'card_reader': 'Card Reader',
            'temperature': 'Temperature',
            'temperature_humidity': 'Temperature/Humidity/Pressure',
            'tracker': 'Tracker'
        };
        const sensorTypeLabel = isAccountLevel
            ? t('alarmTemplates.missingCheckin')
            : (sensorTypeLabels[template.sensor_type] || template.sensor_type);
        
        // Format configuration based on alarm type
        let configHtml = '';
        
        if (isAccountLevel) {
            const config = template.missing_checkin_config || {};
            const recipientCount = (template.actions?.email_recipient_user_ids || []).length;
            configHtml = `
                <div class="text-sm grid grid-cols-2 gap-2">
                    <div>
                        <span class="text-gray-500">${t('alarmTemplates.reportTime')}</span>
                        <span class="ml-1 font-medium">${config.check_time || '-'}</span>
                    </div>
                    <div>
                        <span class="text-gray-500">${t('alarmTemplates.period')}</span>
                        <span class="ml-1 font-medium">${config.lookback_hours || 24} ${t('alarmTemplates.hours')}</span>
                    </div>
                    <div class="col-span-2">
                        <span class="text-gray-500">${t('alarmTemplates.days')}</span>
                        <span class="ml-1">${formatWeekdays(config.weekdays)}</span>
                    </div>
                    <div class="col-span-2">
                        <span class="text-gray-500">${t('alarmTemplates.emailRecipients')}</span>
                        <span class="ml-1">${recipientCount} ${t('alarmTemplates.users')}</span>
                    </div>
                </div>
            `;
        } else if (template.temperature_config) {
            // Temperature alarm configuration
            const alarmTypeLabels = {
                'high_temperature': 'High Temperature',
                'low_temperature': 'Low Temperature'
            };
            const alarmTypeLabel = alarmTypeLabels[template.temperature_config.alarm_type] || template.temperature_config.alarm_type;
            
            configHtml = `
                <div class="text-sm">
                    <div class="grid grid-cols-2 gap-2">
                        <div>
                            <span class="text-gray-500">Type:</span>
                            <span class="ml-1 font-medium">${alarmTypeLabel}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Limit:</span>
                            <span class="ml-1 font-medium">${template.temperature_config.temperature_limit}°C</span>
                        </div>
                        <div>
                            <span class="text-gray-500">Hysteresis:</span>
                            <span class="ml-1 font-medium">${template.temperature_config.hysteresis}°C</span>
                        </div>
                    </div>
                </div>
            `;
        } else {
            // Registration alarm with schedules
            const schedules = template.schedules || [];
            configHtml = schedules.length > 0 ? schedules.map((schedule, index) => `
                <div class="text-sm ${index > 0 ? 'mt-2 pt-2 border-t border-gray-100' : ''}">
                    <div class="flex items-center gap-2 text-xs text-gray-500 mb-1">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        ${t('alarmTemplates.schedule')} ${index + 1}
                    </div>
                    <div class="grid grid-cols-2 gap-2 text-sm ml-5">
                        <div>
                            <span class="text-gray-500">${t('alarmTemplates.check')}</span>
                            <span class="ml-1 font-medium">${schedule.check_time}</span>
                        </div>
                        <div>
                            <span class="text-gray-500">${t('alarmTemplates.max')}</span>
                            <span class="ml-1 font-medium">${schedule.max_time_since_registration}min</span>
                        </div>
                        <div class="col-span-2">
                            <span class="text-gray-500">${t('alarmTemplates.days')}</span>
                            <span class="ml-1">${formatWeekdays(schedule.weekdays)}</span>
                        </div>
                    </div>
                </div>
            `).join('') : `<p class="text-sm text-gray-400">${t('alarmTemplates.noSchedules')}</p>`;
        }
        
        // Update badge to show number of schedules (only for registration alarms)
        const schedules = template.schedules || [];
        const scheduleCountBadge = schedules.length > 1 ? `
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                ${schedules.length} ${t('alarmTemplates.schedules')}
            </span>
        ` : '';
        
        return `
        <div class="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
            <div class="flex items-start justify-between">
                <div class="flex-1">
                    <div class="flex items-center gap-3 flex-wrap">
                        <h3 class="text-lg font-medium text-gray-900">${template.template_name}</h3>
                        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            ${sensorTypeLabel}
                        </span>
                        ${template.timezone ? `
                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                                ${template.timezone}
                            </span>
                        ` : ''}
                        ${scheduleCountBadge}
                        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                            ${isAccountLevel
                                ? `${template.assigned_account_count || 0} ${t('alarmTemplates.accounts')}`
                                : `${template.assigned_sensor_count || 0} ${t('alarmTemplates.sensors')}`}
                        </span>
                    </div>
                    ${template.description ? `<p class="mt-1 text-sm text-gray-500">${template.description}</p>` : ''}
                    
                    <div class="mt-3">
                        <div class="bg-gray-50 rounded-lg p-3">
                            ${configHtml}
                        </div>
                    </div>
                </div>
                
                <div class="flex items-center gap-2 ml-4">
                    ${isAccountLevel ? `
                        <button onclick="window.showAccountAlarmAssignments('${template._id}')" class="px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100">
                            ${t('alarmTemplates.manageAccounts')}
                        </button>
                    ` : ''}
                    <button onclick="window.showEditAlarmTemplateModal('${template._id}')" class="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100" title="Edit template">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                    <button onclick="window.deleteAlarmTemplate('${template._id}')" class="p-2 text-red-400 hover:text-red-600 rounded-md hover:bg-red-50" title="Delete template">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
        `;
    }).join('');
}

// ============================================================================
// Delete Function
// ============================================================================

export async function deleteAlarmTemplate(templateId) {
    if (!confirm('Are you sure you want to delete this alarm template?')) {
        return;
    }

    try {
        const response = await fetch(`/api/alarm-templates/templates/${templateId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${auth.token}` }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to delete template');
        }

        // Reload templates
        await loadAlarmTemplates();

    } catch (error) {
        console.error('Error deleting template:', error);
        showError(error.message);
    }
}

// ============================================================================
// Global Exports (for onclick handlers)
// ============================================================================

window.filterAlarmTemplates = filterAlarmTemplates;
window.deleteAlarmTemplate = deleteAlarmTemplate;
window.loadAlarmTemplates = loadAlarmTemplates;
